import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, IndexTable, Select, Text, TextField } from "@shopify/polaris";
import { useNavigate, useOutletContext } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminStoreSummary } from "../../app/models/Admin";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};


export default function AdminStoresPage() {
  type ActiveFilter = "all" | "active" | "inactive";
  const pageSize = 50;
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [stores, setStores] = useState<AdminStoreSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStores = useCallback(async (pageToLoad: number, queryToLoad: string, filterToLoad: ActiveFilter) => {
    setIsLoading(true);
    setError(null);

    try {
      const isActive = filterToLoad === "all" ? undefined : filterToLoad === "active";
      const response = await agent.Admin.stores(token, queryToLoad, pageToLoad, pageSize, isActive);
      setStores(response.items);
      setTotalCount(response.totalCount);
      setPage(response.page);
    } catch {
      setError("Unable to load stores. Please login again.");
      onUnauthorized();
    } finally {
      setIsLoading(false);
    }
  }, [token, onUnauthorized, pageSize]);

  useEffect(() => {
    loadStores(1, "", "all");
  }, [loadStores]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [totalCount, pageSize]);

  const handleSearch = useCallback(() => {
    void loadStores(1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, query]);

  const handleRefresh = useCallback(() => {
    void loadStores(page, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query]);

  const handleNextPage = useCallback(() => {
    if (page >= totalPages) return;
    void loadStores(page + 1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query, totalPages]);

  const handlePreviousPage = useCallback(() => {
    if (page <= 1) return;
    void loadStores(page - 1, query.trim(), activeFilter);
  }, [activeFilter, loadStores, page, query]);

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <TextField
          label="Search by domain, myshopify domain, shop name, or email"
          value={query}
          onChange={setQuery}
          onBlur={handleSearch}
          autoComplete="off"
          placeholder="example.myshopify.com"
        />
        <Select
          label="Status"
          options={[
            { label: "All stores", value: "all" },
            { label: "Active only", value: "active" },
            { label: "Inactive only", value: "inactive" }
          ]}
          value={activeFilter}
          onChange={(value) => {
            const nextFilter = value as ActiveFilter;
            setActiveFilter(nextFilter);
            void loadStores(1, query.trim(), nextFilter);
          }}
        />
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button onClick={handleSearch} loading={isLoading}>Search</Button>
          <Button onClick={() => { setQuery(""); setActiveFilter("all"); void loadStores(1, "", "all"); }} disabled={isLoading}>Clear</Button>
        </div>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {stores.length === 0 ? (
          <Text as="p" variant="bodyMd">No stores found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "store", plural: "stores" }}
            itemCount={stores.length}
            selectable={false}
            headings={[
              { title: "Name" },
              { title: "Domain" },
              { title: "Email" },
              { title: "Is Active" },
              { title: "Last Sync" },
              { title: "Actions" },
            ]}
          >
            {stores.map((store, index) => (
              <IndexTable.Row id={`${store.shopId}`} key={store.shopId} position={index}>
                <IndexTable.Cell>
                  <Text as="span" fontWeight="semibold">{store.name || store.domain || `Shop ${store.shopId}`}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{store.domain || store.myShopifyDomain || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{store.email || store.contactEmail || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{store.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Inactive</Badge>}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(store.lastSyncAt)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${store.shopId}`)}>View details</Button>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Text as="p" variant="bodyMd">Page {page} of {totalPages} • {totalCount} stores total</Text>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button onClick={handlePreviousPage} disabled={isLoading || page <= 1}>Previous</Button>
            <Button onClick={handleNextPage} disabled={isLoading || page >= totalPages}>Next</Button>
            <Button onClick={handleRefresh} loading={isLoading}>Refresh stores</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
